// Firma belgeleri için önizleme içi onay/red paneli (sunucu karar uç noktasını çağırır)
import { useState } from 'react';
import { CheckCircle2, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { DocReviewPanel } from './DocReviewPanel';

// personLabel verilirse (ör. İSG Katip Uzman) onaydan önce kişinin adı soyadı girilir
// sicilMode: 'enter' (SGK Hizmet Listesi – sicil son 4 hane girilir) | 'match' (Katip sözleşmesi – refSicil ile karşılaştırılır)
export const DocDecisionPanel = ({ url, docLabel, validity, onDecided, personLabel, initialPerson = '', onPersonSaved, suggestions = [], hasDate = true, approved = false, templates = [], sicilMode, refSicil = '', initialSicil = '', onSicilSaved, docSicil = '', extra = null, onApproved, extraBody }) => {
  const [person, setPerson] = useState(initialPerson);
  const [listOpen, setListOpen] = useState(false);
  const q = person.trim().toLocaleLowerCase('tr-TR');
  const matches = suggestions.filter(n => !q || n.toLocaleLowerCase('tr-TR').includes(q));
  const name = person.trim();
  const [sicil, setSicil] = useState(sicilMode === 'enter' ? initialSicil : '');
  const [editSicil, setEditSicil] = useState(false);
  const sicilValid = /^\d{4}$/.test(sicil);
  const sicilBlock = !sicilMode ? null
    : sicilMode === 'match' && !refSicil ? "Önce SGK Hizmet Listesi'ni onaylayın, sicil girin"
    : !sicilValid ? 'SGK sicil numarasının son 4 hanesini girin'
    : sicilMode === 'match' && sicil !== refSicil ? 'Sicil eşleşmiyor: katip bu işyerine ait değil'
    : null;

  const send = async (action, body) => {
    if (personLabel && !name) {
      toast.error(`Lütfen önce ${personLabel} alanını doldurun`);
      return;
    }
    if (action === 'approve' && sicilBlock) { toast.error(sicilBlock); return; }
    try {
      const res = await axios.put(url, { action, reason: '', validity: '', ...(sicilMode === 'enter' && action === 'approve' ? { sicil_last4: sicil } : {}), ...(action === 'approve' ? extraBody : {}), ...body });
      if (res.data?.auto_approved?.length) toast.success(`Sicil eşleşti: ${res.data.auto_approved.map(k => (k === 'isg_katip_uzman' ? 'İSG Katip Uzman' : 'İSG Katip Hekim')).join(' ve ')} otomatik onaylandı`);
      if (personLabel) onPersonSaved(name);
      if (sicilMode && action === 'approve') onSicilSaved(sicil);
      if (action === 'approve') await onApproved?.(body.validity);
      toast.success(action === 'approve' ? `${docLabel} onaylandı` : `${docLabel} eksik/hatalı olarak işaretlendi`);
      onDecided(res.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'İşlem başarısız');
    }
  };

  const sicilInput = (
    <input
      value={sicil} onChange={(e) => setSicil(e.target.value.replace(/\D/g, '').slice(0, 4))} inputMode="numeric" placeholder="1234"
      className="h-8 w-24 rounded-md border border-slate-200 bg-white px-2 text-center font-mono text-sm tracking-widest" data-testid="doc-decision-sicil"
    />
  );
  const sicilField = sicilMode && (
    <div className="space-y-1" data-testid="doc-decision-sicil-field">
      {sicilMode === 'match' && (
        <p className="rounded border border-sky-200 bg-sky-50 px-2 py-1 text-xs text-sky-800" data-testid="doc-decision-ref-sicil">
          SGK Hizmet Listesi sicil (son 4 hane): <span className="font-mono font-bold">{refSicil || '—'}</span>
        </p>
      )}
      <label className="text-xs text-slate-500">{sicilMode === 'enter' ? 'SGK İşyeri Sicil No (son 4 hane) *' : 'Sözleşmedeki SGK Sicil No (son 4 hane) *'}</label>
      {sicilInput}
      {sicilMode === 'match' && sicilValid && refSicil && (sicil === refSicil
        ? <p className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700" data-testid="doc-decision-sicil-ok">Sicil eşleşti: katip bu işyerine aittir</p>
        : <p className="rounded border-2 border-rose-400 bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-700" data-testid="doc-decision-sicil-mismatch">Sicil eşleşmiyor! Katip bu işyerine ait değil. Onay verilemez.</p>)}
      {sicilMode === 'match' && !refSicil && <p className="rounded border-2 border-rose-400 bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-700" data-testid="doc-decision-sicil-missing">Önce SGK Hizmet Listesi'ni onaylayın, sicil girin.</p>}
    </div>
  );

  return (
    <>
    {approved && sicilMode === 'enter' && (
      <div className="space-y-2 rounded-lg border border-sky-200 bg-sky-50/50 p-3" data-testid="doc-decision-sicil-save-box">
        <label className="text-xs text-slate-600">SGK İşyeri Sicil No (son 4 hane) *</label>
        {initialSicil && !editSicil ? (
          <div className="flex items-center justify-between gap-2" data-testid="doc-decision-sicil-saved">
            <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-4 w-4" />Kayıtlı: <span className="font-mono text-sm tracking-widest text-slate-800">{initialSicil}</span></span>
            <button type="button" onClick={() => { setSicil(initialSicil); setEditSicil(true); }} className="rounded-md px-2 py-1 text-xs text-sky-700 hover:bg-sky-100" data-testid="doc-decision-sicil-edit">Değiştir</button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {sicilInput}
            <button type="button" disabled={!sicilValid || sicil === initialSicil} onClick={() => { onSicilSaved(sicil); setEditSicil(false); toast.success('Sicil kaydedildi'); }}
              className="h-8 rounded-md bg-sky-600 px-3 text-xs font-medium text-white hover:bg-sky-700 disabled:opacity-40" data-testid="doc-decision-sicil-save">Sicili Kaydet</button>
          </div>
        )}
      </div>
    )}
    {approved && sicilMode === 'match' && (
      <div className="space-y-1.5 rounded-lg border border-sky-200 bg-sky-50/50 p-3 text-xs" data-testid="doc-decision-sicil-summary">
        <p className="font-medium text-slate-700">SGK Sicil No Kontrolü (son 4 hane)</p>
        <div className="flex justify-between"><span className="text-slate-600">SGK Hizmet Listesi:</span><span className="font-mono font-bold text-slate-800" data-testid="sicil-summary-sgk">{refSicil || '—'}</span></div>
        <div className="flex justify-between"><span className="text-slate-600">{docLabel} sözleşmesi:</span><span className="font-mono font-bold text-slate-800" data-testid="sicil-summary-katip">{docSicil || '—'}</span></div>
        {refSicil && docSicil ? (refSicil === docSicil
          ? <p className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1 font-semibold text-emerald-700" data-testid="sicil-summary-ok">Sicil eşleşti: katip bu işyerine aittir</p>
          : <p className="rounded border-2 border-rose-400 bg-rose-50 px-2 py-1 font-semibold text-rose-700" data-testid="sicil-summary-mismatch">Sicil eşleşmiyor! Katip bu işyerine ait değil.</p>)
          : <p className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-800" data-testid="sicil-summary-missing">{!refSicil ? "SGK Hizmet Listesi sicili girilmemiş." : 'Sözleşmedeki sicil henüz kaydedilmemiş. Aşağıya girip kaydedin.'}</p>}
        {refSicil && !docSicil && (
          <div className="flex items-center gap-2 pt-1">
            {sicilInput}
            <button type="button" disabled={!sicilValid} onClick={() => { onSicilSaved(sicil); toast[sicil === refSicil ? 'success' : 'error'](sicil === refSicil ? 'Sicil kaydedildi: eşleşti' : 'Sicil kaydedildi: EŞLEŞMİYOR'); }}
              className="h-8 rounded-md bg-sky-600 px-3 text-xs font-medium text-white hover:bg-sky-700 disabled:opacity-40" data-testid="sicil-summary-save">Sicili Kaydet</button>
          </div>
        )}
      </div>
    )}
    {approved && extra}
    <DocReviewPanel
      approved={approved}
      approveDisabled={!!sicilBlock}
      templates={templates}
      docLabel={docLabel}
      initialDate={validity}
      dateLabel={hasDate ? 'Geçerlilik tarihi' : undefined}
      approveHint={personLabel && !name ? `Onaylamak için ${personLabel} girin` : sicilBlock || undefined}
      onApprove={(date) => send('approve', { validity: date })}
      onReject={({ deficiencies, reason }) => send('reject', { reason: [...deficiencies, reason].filter(Boolean).join(', ') })}
    >
      {extra}
      {personLabel && (
        <div className="space-y-1">
          <label className="text-xs text-slate-500">{personLabel} *</label>
          <div className="relative">
            <input
              value={person} onChange={(e) => { setPerson(e.target.value); setListOpen(true); }} placeholder="Adı ve soyadı yazın veya listeden seçin..."
              onBlur={() => setTimeout(() => setListOpen(false), 150)}
              className="h-8 w-full rounded-md border border-slate-200 bg-white pl-2 pr-8 text-xs" autoComplete="off"
              data-testid="doc-decision-person"
            />
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => setListOpen(o => !o)} className="absolute right-1 top-1 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Önceki kayıtlar" data-testid="doc-decision-person-toggle">
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${listOpen ? 'rotate-180' : ''}`} />
            </button>
            {listOpen && (
              <div className="absolute z-20 mt-1 max-h-44 w-full overflow-y-auto rounded-md border bg-white py-1 shadow-lg" data-testid="doc-decision-person-list">
                {matches.length ? matches.map(n => (
                  <button key={n} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { setPerson(n); setListOpen(false); }}
                    className="block w-full px-2 py-1.5 text-left text-xs text-slate-700 hover:bg-sky-50" data-testid={`doc-decision-person-option-${n}`}>{n}</button>
                )) : <p className="px-2 py-1.5 text-xs text-slate-400">{suggestions.length ? 'Eşleşen kayıt yok, yeni isim olarak kaydedilecek' : 'Önceki kayıt yok'}</p>}
              </div>
            )}
          </div>
        </div>
      )}
      {sicilField}
    </DocReviewPanel>
    </>
  );
};
